/**
 * Agent service: Decomposer
 * Breaks down doctor's clinical question into assessment goals
 */

import { GROQ_MODEL, API_CONFIG } from "../config/constants";

const buildGroqError = (status, statusText, payloadText, payloadJson) => {
  const apiMessage = payloadJson?.error?.message || payloadText || statusText || "Unknown Groq error";
  const message = String(apiMessage);
  return new Error(`Groq API error ${status}: ${message}`);
};

/**
 * Call Groq API with OpenAI-compatible format
 * @param {string} systemPrompt
 * @param {string} userPrompt
 * @param {number} maxTokens
 * @returns {Promise<string>}
 */
const callGroq = async (systemPrompt, userPrompt, maxTokens = API_CONFIG.groq.defaultMaxTokens) => {
  const apiKey = process.env.REACT_APP_GROQ_API_KEY;
  
  if (!apiKey) {
    throw new Error("❌ Groq API key not configured. Add REACT_APP_GROQ_API_KEY to .env.local");
  }

  const requestBody = {
    model: GROQ_MODEL,
    messages: [
      {
        role: "system",
        content: systemPrompt,
      },
      {
        role: "user",
        content: userPrompt,
      },
    ],
    max_tokens: maxTokens,
    temperature: 0.7,
  };

  try {
    const response = await fetch(`${API_CONFIG.groq.baseUrl}/chat/completions`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${apiKey}`,
      },
      body: JSON.stringify(requestBody),
    });

    if (response.ok) {
      const data = await response.json();
      return data?.choices?.[0]?.message?.content || "";
    }

    const payloadText = await response.text();
    let payloadJson = null;
    try {
      payloadJson = JSON.parse(payloadText);
    } catch (_parseErr) {
      payloadJson = null;
    }

    throw buildGroqError(response.status, response.statusText, payloadText, payloadJson);
  } catch (error) {
    if (error.message.includes("API key")) {
      throw error;
    }
    throw new Error(`Groq API call failed: ${error.message}`);
  }
};

/**
 * Decompose a question into 3-5 assessment goals
 * @param {string} questionText - Doctor's natural language question
 * @returns {Promise<string[]>} - Array of goal strings
 */
export const decomposerAgent = async (questionText) => {
  const system = `You are a clinical AI assistant. Given a therapist's natural language question about a patient, extract 3–5 focused assessment goals. Respond ONLY with a JSON array like: ["goal 1", "goal 2", "goal 3"]. No other text.`;

  try {
    const raw = await callGroq(system, `Doctor's question: "${questionText}"`);
    const clean = raw.replace(/```json|```/g, "").trim();
    return JSON.parse(clean);
  } catch (error) {
    // Fallback goals on error
    return [
      "Understand current emotional state",
      "Assess sleep quality and patterns",
      "Explore social interactions this week",
    ];
  }
};

export { callGroq };
